import { srtTimecodeToSeconds } from '@bynder-studio/render-core';

const SRT_TIMECODE_REGEX = /^(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})$/;

export type SrtContentStatus = 'valid' | 'fixable' | 'invalid';

/** A cue block represented as its lines: `[counter, timecode, ...text]`. */
type SrtBlock = string[];

/**
 * A single SRT rule.
 *
 * - `isSatisfied` reports whether the parsed blocks already comply.
 * - `fix`, when present, deterministically repairs a violation. A rule without a
 *   `fix` is a hard structural constraint: a violation makes the content invalid.
 */
type SrtRule = {
    name: string;
    isSatisfied: (blocks: readonly SrtBlock[]) => boolean;
    fix?: (blocks: readonly SrtBlock[]) => SrtBlock[];
};

/** Strip a leading BOM, normalize CRLF to LF, and trim surrounding whitespace. */
function normalizeSrtWhitespace(srtContent: string): string {
    return srtContent
        .replace(/^\uFEFF/, '')
        .replace(/\r\n/g, '\n')
        .trim();
}

/** Parse content into blocks of lines, after whitespace normalization. */
function parseSrtBlocks(srtContent: string): SrtBlock[] {
    return normalizeSrtWhitespace(srtContent)
        .split(/\n\n+/)
        .map((block) => block.split('\n'));
}

/** Serialize blocks of lines back into a canonical SRT string. */
function serializeSrtBlocks(blocks: readonly SrtBlock[]): string {
    return blocks.map((lines) => lines.join('\n')).join('\n\n');
}

function counterOf(block: SrtBlock): string {
    return block[0] ?? '';
}

function timecodeOf(block: SrtBlock): string {
    return block[1] ?? '';
}

function textOf(block: SrtBlock): string {
    return block.slice(2).join('\n');
}

function isCounterLine(line: string): boolean {
    return /^\d+$/.test(line.trim());
}

function canonicalTimecodeLine(line: string): string {
    const match = line.trim().match(SRT_TIMECODE_REGEX);

    return match ? `${match[1]} --> ${match[2]}` : line;
}

/**
 * The ordered set of SRT rules.
 *
 * Structural rules (no `fix`) come first: a violation means the content is
 * `invalid`. Fixable rules follow: a violation means the content is `fixable`
 * and the rule's `fix` can repair it. Add or refine individual rules here.
 */
const SRT_RULES: readonly SrtRule[] = [
    {
        name: 'min-lines',
        isSatisfied: (blocks) => blocks.every((block) => block.length >= 2),
    },
    {
        name: 'counter-line',
        isSatisfied: (blocks) => blocks.every((block) => isCounterLine(counterOf(block))),
    },
    {
        name: 'timecode-line',
        isSatisfied: (blocks) =>
            blocks.every((block) => {
                const match = timecodeOf(block).trim().match(SRT_TIMECODE_REGEX);

                return match !== null && srtTimecodeToSeconds(match[1]) <= srtTimecodeToSeconds(match[2]);
            }),
    },
    {
        name: 'sequential-ids',
        isSatisfied: (blocks) => blocks.every((block, index) => counterOf(block) === String(index + 1)),
        fix: (blocks) => blocks.map((block, index) => [String(index + 1), ...block.slice(1)]),
    },
    {
        name: 'canonical-timecode',
        isSatisfied: (blocks) =>
            blocks.every((block) => timecodeOf(block) === canonicalTimecodeLine(timecodeOf(block))),
        fix: (blocks) =>
            blocks.map((block) => [counterOf(block), canonicalTimecodeLine(timecodeOf(block)), ...block.slice(2)]),
    },
    {
        name: 'trimmed-text',
        isSatisfied: (blocks) =>
            blocks.every((block) => {
                const text = textOf(block);

                return block.length <= 2 ? text === '' : text !== '' && text === text.trim();
            }),
        fix: (blocks) =>
            blocks.map((block) => {
                const text = textOf(block).trim();

                // Collapses multi-line text into a single element; serializeSrtBlocks
                // rejoins it with `\n`, so the emitted string is unchanged.
                return text ? [counterOf(block), timecodeOf(block), text] : [counterOf(block), timecodeOf(block)];
            }),
    },
];

/**
 * Apply every fixable rule to structurally-sound SRT content, returning a
 * canonical string. Whitespace and line endings are normalized by parsing; the
 * rules renumber ids, canonicalize timecodes, and trim cue text.
 *
 * Only call this on content that `classifySrtContent` reports as `fixable` (or
 * already `valid`).
 */
export function fixSrtContent(srtContent: string): string {
    const blocks = SRT_RULES.reduce<SrtBlock[]>(
        (current, rule) => (rule.fix ? rule.fix(current) : current),
        parseSrtBlocks(srtContent),
    );

    return serializeSrtBlocks(blocks);
}

/**
 * Classify SRT content as `valid`, `fixable`, or `invalid`.
 *
 * - `valid` — a non-empty string already in canonical form.
 * - `fixable` — structurally sound but not canonical; `fixSrtContent` repairs it.
 * - `invalid` — not a string, empty, or violating a structural (non-fixable) rule.
 */
export function classifySrtContent(srtContent: unknown): SrtContentStatus {
    if (typeof srtContent !== 'string' || normalizeSrtWhitespace(srtContent) === '') {
        return 'invalid';
    }

    const blocks = parseSrtBlocks(srtContent);
    // Non-canonical whitespace/line endings are repaired at the parse boundary.
    let fixable = srtContent !== serializeSrtBlocks(blocks);

    for (const rule of SRT_RULES) {
        if (rule.isSatisfied(blocks)) {
            continue;
        }

        if (!rule.fix) {
            return 'invalid';
        }

        fixable = true;
    }

    return fixable ? 'fixable' : 'valid';
}
